/**
 * Share links, display names, and invite acceptance.
 *
 * The sharer's half of the flow lives in one expanding block on Home (D51),
 * not a route, so there's no multi-step wizard state to model here - just the
 * three calls that block makes plus the one the lander makes.
 */

import { apiClient } from './apiClient';
import { ensureToken } from './tokenService';
import { getPlayDate } from '../utils/dateUtils';

/**
 * Set the current user's display name.
 *
 * A write, so it goes through `ensureToken()` like every other one - a visitor
 * naming themselves on an invite lander may not have a record yet, and that's
 * the moment one should come into being.
 *
 * @param {string} username
 * @returns {Promise<{username: string}>}
 */
export const setUsername = async (username) => {
  await ensureToken();
  return apiClient.put('/profile/username', { username });
};

/**
 * Store the card and mint (or reuse) today's share link.
 *
 * Called when the share block is **expanded**, not when Copy is pressed
 * (D52). That ordering is deliberate: it leaves the URL sitting in hand as a
 * plain string, so the clipboard write can happen synchronously inside the
 * click handler. An `await` between the user's gesture and
 * `navigator.clipboard.writeText` loses the gesture context and iOS Safari
 * silently drops the write.
 *
 * The server reuses the day's existing record when `scored` hasn't changed,
 * so re-expanding costs one request and no upload.
 *
 * @param {Object} options
 * @param {string} options.image PNG data URL from renderShareCard
 * @param {Array<{gameId: string|number, score: *}>} options.scored
 * @returns {Promise<{shareId: string, url: string, imageUrl: string, reused: boolean}>}
 */
export const createShare = async ({ image, scored }) => {
  await ensureToken();
  return apiClient.post('/shares', {
    image,
    scored,
    play_date: getPlayDate(),
  });
};

/**
 * Read a share for the lander. No token: arrival must create nothing (D48),
 * and this is the one endpoint a complete stranger hits.
 * @param {string} shareId
 * @returns {Promise<Object>}
 */
export const getShare = async (shareId) => {
  return apiClient.get(`/shares/${encodeURIComponent(shareId)}?date=${getPlayDate()}`);
};

/**
 * Connect the caller to the person whose link they followed.
 *
 * Idempotent server-side, which matters because the client fires it whenever a
 * user id appears and that can happen more than once across reloads and an
 * OAuth round trip. Does **not** call `ensureToken()` on purpose: this must
 * only run for someone who already has a record, never create one, or merely
 * opening a link would make an account.
 *
 * @param {string} shareId
 * @returns {Promise<{connected: boolean, username: ?string, isNew: boolean}>}
 */
export const acceptInvite = async (shareId) => {
  return apiClient.post('/invites/accept', { shareId });
};
